import React, { useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Collapse, Stack, Typography } from '@mui/material';

import ChecklistStatusDot from '../../reportCards/ChecklistStatusDot';

import ValidityComparisonChain from './ValidityComparisonChain';
import type { BundledExperienceRowView } from './types';

interface Props {
  row: BundledExperienceRowView;
}

export default function ValiditySection({ row }: Props) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const verdict = row.validityOk
    ? 'Experience dates match or exceed travel validity dates.'
    : 'Experience dates do not match or exceed travel validity dates.';

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        variant="caption"
        fontWeight={700}
        color="text.secondary"
        sx={{ letterSpacing: '0.08em', display: 'block', mb: 1 }}
      >
        VALIDITY DATE
      </Typography>
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setDetailsOpen((open) => !open)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setDetailsOpen((open) => !open);
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          borderRadius: 1,
          px: 1,
          py: 0.75,
          '&:hover': { bgcolor: 'grey.200' },
        }}
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: 2, flex: 1, minWidth: 0 }}>
          {renderCell('Experience validity', `${row.bookBy ?? '—'} – ${row.travelTo ?? '—'}`)}
          {renderCell('Travel validity', `${row.hotelBookBy ?? '—'} – ${row.hotelTravelTo ?? '—'}`)}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'nowrap' }}>
              Validity
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <ChecklistStatusDot hideLabel status={row.validityOk ? 'approved' : 'requires approval'} />
              <Typography variant="body2" sx={{ overflowWrap: 'break-word' }}>
                {verdict}
              </Typography>
            </Stack>
          </Box>
        </Box>
        <ExpandMoreIcon
          fontSize="small"
          sx={{
            flexShrink: 0,
            ml: 1,
            transform: detailsOpen ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.15s',
          }}
        />
      </Box>
      <Collapse in={detailsOpen}>
        <Box sx={{ mx: 2, mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
          <ValidityComparisonChain
            bookByComparison={row.bookByComparison}
            bundledBookBy={row.bookBy}
            bundledTravelTo={row.travelTo}
            hotelBookBy={row.hotelBookBy}
            hotelTravelTo={row.hotelTravelTo}
            travelToComparison={row.travelToComparison}
          />
        </Box>
      </Collapse>
    </Box>
  );
}

function renderCell(label: string, value: string) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'nowrap' }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  );
}
